import { ContentNode, ContentType } from '@amzn/awscat-pattern-library-service-model';
import { useContent } from '../v2-hooks/useContent';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';

/** Exposed state for the Pattern editor. */
export interface PatternEditorViewModel {
  /** Ordered Pattern sections from `content.contentData.contentSections`. */
  readonly sections: readonly ContentNode[];
  /** Content ID for image upload and comment anchoring. */
  readonly contentId: string;
}

/**
 * ViewModel hook for the Pattern editor. Renders the Pattern's flat list of
 * sections. Template-driven section reconciliation runs in the background
 * via the AdhocMigration framework registered with `ContentEditorService`,
 * not here.
 *
 * Returns undefined while content or the document handle is not loaded — the
 * view renders nothing during that transient state rather than crashing. Still
 * throws when loaded content is not a Pattern (the wrong editor was mounted).
 */
export function usePatternEditorViewModel(): PatternEditorViewModel | undefined {
  const content = useContent();
  const loadedDocument = useLoadedDocument();

  // A parent-teardown render can transiently clear content/handle before this
  // view unmounts; return undefined so the view renders null instead of throwing.
  // The two can also update out of step under React 17's unbatched events.
  if (!content || !loadedDocument) return undefined;

  const contentType = content.contentMetadata.contentType;
  if (contentType !== ContentType.Pattern) {
    throw new Error(`PatternEditor only supports Pattern content (got ${contentType}).`);
  }

  const contentId = loadedDocument.contentId;

  return {
    sections: content.contentData.contentSections ?? [],
    contentId,
  };
}
